import Logger from './logger';
import { deferUntilPageVisible } from './page_visibility';
import { getDeviceType } from './utils';
import constants from './constants';

/**
 * Singleton observers for legacy mode - keyed by threshold
 * In legacy mode, we need separate observers for:
 * - Slot rendering (one threshold, shared by all slots)
 * - Auction triggering (different threshold, shared by all auctions)
 * @type {Map<string, IntersectionObserver>}
 */
const _legacyObservers = new Map();

/**
 * Store callbacks for elements, keyed by element
 * Each element can have multiple callbacks with different thresholds
 * Structure: Map<Element, Array<{threshold: string, callback: Function, settings: object}>>
 * @type {Map<Element, Array<{threshold: string, callback: Function, settings: object}>>}
 */
const _elementCallbacks = new Map();

/**
 * Per-threshold observers for smart lazy loading mode
 * Keyed by threshold string, shared across slots with same threshold
 * @type {Map<string, IntersectionObserver>}
 */
const _perThresholdObservers = new Map();

/**
 * Default threshold if nothing else is specified
 */
const DEFAULT_THRESHOLD = '100px 0px 200px 0px';

/**
 * Check if smart lazy loading is enabled in settings
 *
 * @param {object} settings - Settings object (may be undefined)
 * @return {boolean} True if smart lazy loading is enabled
 */
export function isSmartLazyLoadingEnabled(settings) {
  return settings?.smartLazyLoad?.enabled === true;
}

export default class ObserverIntersection {
  /**
   * Watch an element and execute a callback when it approaches the viewport
   * Uses either singleton observer (legacy) or per-threshold observer (smart mode)
   *
   * @param {Element} element - The DOM element to watch
   * @param {string} threshold - The rootMargin threshold (e.g., '300px')
   * @param {Function} callback - Callback to execute when element approaches viewport
   * @param {object} options - Optional settings to check for lazyLoad.enabled
   * @return {undefined}
   */
  static watchElement(element, threshold, callback, options = {}) {
    if (!element) {
      Logger.log('Cannot watch element - element not found');
      return;
    }

    // Check if smart lazy loading is enabled
    const settings = options.settings || options.slot?.app?.settings || {};
    const usePerSlotObservers = isSmartLazyLoadingEnabled(settings);

    // Store callback per element with its threshold and settings
    // This allows the same element to have different callbacks for different thresholds
    // Settings are stored so we can access canUsePageVisibilityAPI even if __slot__ is missing
    if (!_elementCallbacks.has(element)) {
      _elementCallbacks.set(element, []);
    }
    _elementCallbacks.get(element).push({ threshold, callback, settings });

    if (usePerSlotObservers) {
      // Smart mode: use per-threshold observer (shared across slots with same threshold)
      const observer = getPerThresholdObserver(threshold);
      observer.observe(element);

      // Get page section for logging
      const sectionBoundaries = settings?.smartLazyLoad?.sectionBoundaries;
      const deviceType = getDeviceInfo();
      const pageSection = sectionBoundaries ? getPageSection(element, sectionBoundaries, deviceType) : null;
      const sectionInfo = pageSection ? `, section: ${pageSection}` : '';

      Logger.log(`Starting to watch element with per-threshold observer (threshold: ${threshold}${sectionInfo})`);
    } else {
      // Legacy mode: use threshold-based singleton observers
      // Different thresholds get different observers (e.g., slot rendering vs auction triggering)
      const observer = getLegacyObserver(threshold);
      observer.observe(element);
      Logger.log(`Starting to watch element with legacy observer (threshold: ${threshold})`);
    }
  }

  /**
   * Clear all observers (useful for testing and cleanup)
   * @return {undefined}
   */
  static clearObservers() {
    // Clear legacy observers
    _legacyObservers.forEach(observer => observer.disconnect());
    _legacyObservers.clear();

    // Clear per-threshold observers
    _perThresholdObservers.forEach(observer => observer.disconnect());
    _perThresholdObservers.clear();

    // Clear element callbacks
    _elementCallbacks.clear();
  }
}

/**
 * Normalize threshold string to ensure all values have units
 * IntersectionObserver requires all values to have units (px, %, vh, etc.)
 * Converts "100px 0 600px 0" to "100px 0px 600px 0px"
 * Returns default threshold if input is invalid
 *
 * @param {string} threshold - Threshold string to normalize
 * @return {string} Normalized threshold string (or DEFAULT_THRESHOLD if invalid)
 */
function normalizeThreshold(threshold) {
  if (!threshold || typeof threshold !== 'string') {
    return DEFAULT_THRESHOLD;
  }

  // Split by whitespace and normalize each part
  const parts = threshold.trim().split(/\s+/);
  const normalized = parts.map(part => {
    // If it's just a number (no unit), add 'px' as default
    if (/^-?\d+(?:\.\d+)?$/.test(part)) {
      return `${part}px`;
    }
    // If it already has a unit, return as-is
    return part;
  });

  return normalized.join(' ');
}

/**
 * Get or create a legacy IntersectionObserver (legacy mode)
 * In legacy mode, observers are shared by threshold:
 * - One observer for slot rendering (all slots share it, one threshold)
 * - One observer for auction triggering (all auctions share it, different threshold)
 *
 * @param {string} threshold - The rootMargin value
 * @return {IntersectionObserver}
 */
function getLegacyObserver(threshold) {
  const normalizedThreshold = normalizeThreshold(threshold);

  if (!_legacyObservers.has(normalizedThreshold)) {
    Logger.log(`Creating legacy IntersectionObserver with threshold: ${normalizedThreshold}`);

    const observer = new IntersectionObserver(
      (entries, observer) => {
        Logger.log(`Legacy observer callback triggered (threshold: ${normalizedThreshold})`);

        entries.forEach(function(entry) {
          if (entry.intersectionRatio > 0.0 || entry.isIntersecting) {
            observer.unobserve(entry.target);
            const callbacks = _elementCallbacks.get(entry.target);
            if (callbacks && callbacks.length > 0) {
              // In legacy mode, observers are shared by threshold
              // Only call callbacks that match this observer's threshold
              const matchingCallbacks = callbacks.filter(
                cb => normalizeThreshold(cb.threshold) === normalizedThreshold
              );

              matchingCallbacks.forEach(({ callback, settings }) => {
                // Check settings for page visibility (stored settings from watchElement call)
                const runImmediately = settings?.canUsePageVisibilityAPI === false;

                // Defer callback until page is visible (applies to both slots and auctions)
                deferUntilPageVisible(() => callback(entry.target), {
                  runImmediately: runImmediately,
                });
              });

              // Remove only the callbacks that matched this threshold
              const remainingCallbacks = callbacks.filter(
                cb => normalizeThreshold(cb.threshold) !== normalizedThreshold
              );
              if (remainingCallbacks.length > 0) {
                _elementCallbacks.set(entry.target, remainingCallbacks);
              } else {
                _elementCallbacks.delete(entry.target);
              }
            }
          }
        });
      },
      {
        rootMargin: normalizedThreshold,
      }
    );

    _legacyObservers.set(normalizedThreshold, observer);
  }

  return _legacyObservers.get(normalizedThreshold);
}

/**
 * Get or create a per-threshold IntersectionObserver (smart mode)
 * Observers are shared across slots with the same threshold
 *
 * @param {string} threshold - The rootMargin value
 * @return {IntersectionObserver}
 */
function getPerThresholdObserver(threshold) {
  // Normalize threshold to ensure all values have units
  const normalizedThreshold = normalizeThreshold(threshold);

  // Use normalized threshold as the key for the map
  if (!_perThresholdObservers.has(normalizedThreshold)) {
    Logger.log(`Creating per-threshold IntersectionObserver with threshold: ${normalizedThreshold}`);

    const observer = new IntersectionObserver(
      (entries, observer) => {
        Logger.log(`Per-threshold observer callback triggered (threshold: ${normalizedThreshold})`);

        entries.forEach(function(entry) {
          if (entry.intersectionRatio > 0.0 || entry.isIntersecting) {
            observer.unobserve(entry.target);
            const callbacks = _elementCallbacks.get(entry.target);
            if (callbacks && callbacks.length > 0) {
              // Only call callbacks that match this observer's threshold (use original threshold for matching)
              // This allows the same element to have different callbacks for different thresholds
              const matchingCallbacks = callbacks.filter(cb => {
                // Normalize both thresholds for comparison
                return normalizeThreshold(cb.threshold) === normalizedThreshold;
              });

              matchingCallbacks.forEach(({ callback, settings }) => {
                // Check settings for page visibility (stored settings from watchElement call)
                const runImmediately = settings?.canUsePageVisibilityAPI === false;

                // Defer callback until page is visible (applies to both slots and auctions)
                deferUntilPageVisible(() => callback(entry.target), {
                  runImmediately: runImmediately,
                });
              });

              // Remove only the callbacks that matched this threshold
              const remainingCallbacks = callbacks.filter(cb => {
                return normalizeThreshold(cb.threshold) !== normalizedThreshold;
              });
              if (remainingCallbacks.length > 0) {
                _elementCallbacks.set(entry.target, remainingCallbacks);
              } else {
                _elementCallbacks.delete(entry.target);
              }
            }
          }
        });
      },
      {
        rootMargin: normalizedThreshold,
      }
    );

    _perThresholdObservers.set(normalizedThreshold, observer);
  }

  return _perThresholdObservers.get(normalizedThreshold);
}

/**
 * Resolve device-specific sectionBoundaries from config
 * Supports both device-specific structure and flat structure (backward compatible)
 *
 * @param {object} sectionBoundaries - Section boundary definitions from config
 * @param {string} deviceType - Device type (mobile, mobileIOS, tablet, tabletIOS, desktop)
 * @return {object|null} Resolved section boundaries or null
 */
function resolveSectionBoundaries(sectionBoundaries, deviceType) {
  if (!sectionBoundaries) {
    return null;
  }

  // Check if this is a device-specific structure (has mobile/tablet/desktop keys)
  const deviceKeys = ['mobile', 'mobileIOS', 'tablet', 'tabletIOS', 'desktop', 'default'];
  const hasDeviceKeys = deviceKeys.some(key => sectionBoundaries[key] && typeof sectionBoundaries[key] === 'object');

  if (hasDeviceKeys) {
    // Device-specific structure: resolve by device type
    const baseDeviceType = deviceType.replace('IOS', '');

    // Check iOS-specific device type first
    if (deviceType.includes('IOS') && sectionBoundaries[deviceType]) {
      return sectionBoundaries[deviceType];
    }

    // Fall back to base device type
    if (sectionBoundaries[baseDeviceType]) {
      return sectionBoundaries[baseDeviceType];
    }

    // Fall back to default
    if (sectionBoundaries.default) {
      return sectionBoundaries.default;
    }

    return null;
  }

  // Flat structure (backward compatible) - return as-is
  return sectionBoundaries;
}

/**
 * Determine which page section an element is in based on distance from top
 * Uses sectionBoundaries config to determine section boundaries
 * Supports both device-specific and flat sectionBoundaries structures
 *
 * @param {Element} element - The DOM element to check
 * @param {object} sectionBoundaries - Section boundary definitions from config (can be device-specific or flat)
 * @param {string} [deviceType] - Device type (mobile, mobileIOS, tablet, tabletIOS, desktop). Defaults to 'desktop' if not provided.
 * @return {string|null} Section name (top, topMiddle, middle, middleBottom, bottom) or null
 */
function getPageSection(element, sectionBoundaries, deviceType = 'desktop') {
  if (!element || !sectionBoundaries) {
    return null;
  }

  // Resolve device-specific boundaries if needed
  const resolvedBoundaries = resolveSectionBoundaries(sectionBoundaries, deviceType);
  if (!resolvedBoundaries) {
    return null;
  }

  // Get element's distance from top of document
  const rect = element.getBoundingClientRect();
  const distanceFromTop = rect.top + window.scrollY;
  const viewportHeight = window.innerHeight;

  // Check each section boundary
  const sections = Object.keys(resolvedBoundaries).sort((a, b) => {
    // Sort by min value to check in order
    const aMin = resolvedBoundaries[a].minVh || resolvedBoundaries[a].min || 0;
    const bMin = resolvedBoundaries[b].minVh || resolvedBoundaries[b].min || 0;
    return aMin - bMin;
  });

  for (const sectionName of sections) {
    const boundary = resolvedBoundaries[sectionName];
    let min = 0;
    let max = Infinity;

    // Calculate min boundary (in pixels)
    if (boundary.minVh !== undefined) {
      min = boundary.minVh * viewportHeight;
    } else if (boundary.min !== undefined) {
      min = boundary.min;
    }

    // Calculate max boundary (in pixels)
    if (boundary.maxVh !== undefined) {
      max = boundary.maxVh * viewportHeight;
      // If min and max are equal, treat as inclusive (single point)
      if (max === min) {
        if (distanceFromTop === min) {
          return sectionName;
        }
        continue;
      }
    } else if (boundary.max !== undefined) {
      max = boundary.max;
      // If min and max are equal, treat as inclusive (single point)
      if (max === min) {
        if (distanceFromTop === min) {
          return sectionName;
        }
        continue;
      }
    }

    // Check if element is within this section's boundaries
    // min is inclusive, max is exclusive (unless they're equal, handled above)
    if (distanceFromTop >= min && distanceFromTop < max) {
      return sectionName;
    }
  }

  return null;
}

/**
 * Get device type, including iOS-specific types (mobileIOS, tabletIOS)
 *
 * @return {string} Device type (mobile, mobileIOS, tablet, tabletIOS, desktop)
 */
function getDeviceInfo() {
  const deviceType = getDeviceType();
  const isIOS = constants.IS_IOS;

  // Return iOS-specific device types for mobile and tablet
  if (isIOS && (deviceType === 'mobile' || deviceType === 'tablet')) {
    return `${deviceType}IOS`;
  }

  return deviceType;
}

/**
 * Resolve threshold using cascading priority:
 * 1. Slot-level smartLazyLoadThreshold (highest priority)
 * 2. Settings-level smartLazyLoadThreshold
 * 3. DEFAULT_THRESHOLD (fallback)
 *
 * Within each level, checks:
 * - Page section → Device type (mobileIOS/tabletIOS for iOS, mobile/tablet/desktop otherwise)
 * - Falls back to base device type (mobile/tablet) if iOS-specific not found
 * - Falls back to device type only if no section
 * - Falls back to default if no device type
 *
 * @param {object} slot - Slot object with data and app
 * @param {Element} element - DOM element to determine page section
 * @return {string} Resolved threshold string
 */
export function resolveThreshold(slot, element) {
  const slotConfig = slot.data?.smartLazyLoadThreshold;
  const settingsConfig = slot.app?.settings?.smartLazyLoadThreshold;
  const sectionBoundaries = slot.app?.settings?.smartLazyLoad?.sectionBoundaries;

  // Get device info (returns mobileIOS/tabletIOS for iOS devices, or mobile/tablet/desktop)
  const deviceType = getDeviceInfo();

  // Determine page section (pass deviceType to support device-specific boundaries)
  const pageSection = sectionBoundaries ? getPageSection(element, sectionBoundaries, deviceType) : null;

  // Try slot-level config first
  let threshold = resolveThresholdFromConfig(slotConfig, pageSection, deviceType);

  // Validate threshold is a string (invalid values should fall through to next level)
  if (!threshold || typeof threshold !== 'string') {
    // Fall back to settings-level config
    threshold = resolveThresholdFromConfig(settingsConfig, pageSection, deviceType);
  }

  // Validate again and fall back to default if still invalid
  if (!threshold || typeof threshold !== 'string') {
    threshold = DEFAULT_THRESHOLD;
  }

  return threshold;
}

/**
 * Resolve threshold from a config object using cascading priority
 *
 * Device types supported: mobile, mobileIOS, tablet, tabletIOS, desktop
 * Priority: iOS-specific device type → base device type → default
 *
 * @param {object} config - smartLazyLoadThreshold config object
 * @param {string|null} pageSection - Page section name or null
 * @param {string} deviceType - Device type (mobile, mobileIOS, tablet, tabletIOS, desktop)
 * @return {string|null} Resolved threshold or null if not found
 */
function resolveThresholdFromConfig(config, pageSection, deviceType) {
  if (!config) {
    return null;
  }

  // Extract base device type (mobile/tablet/desktop) for fallback
  const baseDeviceType = deviceType.replace('IOS', '');

  // If page section is specified, check section-specific config
  if (pageSection && config[pageSection]) {
    const sectionConfig = config[pageSection];

    // Check iOS-specific device type first (e.g., mobileIOS, tabletIOS)
    if (deviceType.includes('IOS') && sectionConfig[deviceType]) {
      return sectionConfig[deviceType];
    }

    // Fall back to base device type (e.g., mobile, tablet)
    if (sectionConfig[baseDeviceType]) {
      return sectionConfig[baseDeviceType];
    }

    // Fall back to section default
    if (sectionConfig.default) {
      return sectionConfig.default;
    }
  }

  // If no page section or section config not found, check device type at root level
  // Check iOS-specific device type first
  if (deviceType.includes('IOS') && config[deviceType]) {
    return config[deviceType];
  }

  // Fall back to base device type
  if (config[baseDeviceType]) {
    return config[baseDeviceType];
  }

  // Final fallback to root default
  if (config.default) {
    return config.default;
  }

  return null;
}

/**
 * Parse threshold string and add pixels to top and bottom values
 * Threshold format follows CSS margin syntax:
 * - Single value: "200px" (all sides)
 * - Two values: "100px 200px" (top/bottom, left/right)
 * - Three values: "100px 0px 200px" (top, left/right, bottom)
 * - Four values: "100px 0px 200px 0px" (top, right, bottom, left)
 *
 * @param {string} threshold - Threshold string to modify
 * @param {number} pixelsToAdd - Pixels to add to top and bottom (default: constants.DEFAULT_AUCTION_THRESHOLD_ADJUSTMENT)
 * @return {string} Modified threshold string
 */
function addPixelsToThreshold(threshold, pixelsToAdd = constants.DEFAULT_AUCTION_THRESHOLD_ADJUSTMENT) {
  if (!threshold || typeof threshold !== 'string') {
    return threshold;
  }

  // Ensure pixelsToAdd is a number (parse string values like "100px" or "100")
  const numericPixelsToAdd =
    typeof pixelsToAdd === 'string'
      ? parseFloat(pixelsToAdd.replace(/[^\d.-]/g, '')) || constants.DEFAULT_AUCTION_THRESHOLD_ADJUSTMENT
      : Number(pixelsToAdd) || constants.DEFAULT_AUCTION_THRESHOLD_ADJUSTMENT;

  // Parse threshold: "top right bottom left" or "top bottom" or single value
  const parts = threshold.trim().split(/\s+/);
  const parsed = parts.map(part => {
    const match = part.match(/^(-?\d+(?:\.\d+)?)(px|vh|%)?$/);
    if (!match) return null; // Mark as invalid
    return { value: parseFloat(match[1]), unit: match[2] || 'px' };
  });

  // If any part couldn't be parsed, return original threshold
  if (parsed.some(part => part === null)) {
    return threshold;
  }

  // Handle different threshold formats
  if (parsed.length === 1) {
    // Single value: "200px" - add to it
    const value = parsed[0].value + numericPixelsToAdd;
    return `${value}${parsed[0].unit}`;
  } else if (parsed.length === 2) {
    // Two values: "100px 200px" - add to both (top and bottom)
    const top = parsed[0].value + numericPixelsToAdd;
    const bottom = parsed[1].value + numericPixelsToAdd;
    return `${top}${parsed[0].unit} ${bottom}${parsed[1].unit}`;
  } else if (parsed.length === 3) {
    // Three values: "top left/right bottom" - add to top and bottom, preserve left/right
    const top = parsed[0].value + numericPixelsToAdd;
    const leftRight = parsed[1].value;
    const bottom = parsed[2].value + numericPixelsToAdd;
    return `${top}${parsed[0].unit} ${leftRight}${parsed[1].unit} ${bottom}${parsed[2].unit}`;
  } else if (parsed.length === 4) {
    // Four values: "top right bottom left"
    const top = parsed[0].value + numericPixelsToAdd;
    const bottom = parsed[2].value + numericPixelsToAdd;
    return `${top}${parsed[0].unit} ${parsed[1].value}${parsed[1].unit} ${bottom}${parsed[2].unit} ${parsed[3].value}${
      parsed[3].unit
    }`;
  }

  // If we can't parse it, return as-is
  return threshold;
}

/**
 * Export for testing purposes
 */
export {
  DEFAULT_THRESHOLD,
  getPageSection,
  getDeviceInfo,
  resolveThresholdFromConfig,
  resolveSectionBoundaries,
  addPixelsToThreshold,
};
